import React, { createContext, useContext, useState, useMemo, ReactNode } from "react";

export type PageName =
  | "home"
  | "aircraft"
  | "aircraft-details"
  | "product-details"
  | "login"
  | "register"
  | "dashboard"
  | "cart"
  | "list-aircraft"
  | "list-parts"
  | "list-equipment"
  | "list-experiences"
  | "list-courses"
  | "contact";

interface NavigationContextType {
  page: PageName;
  params: any; // Add this to the interface
  backDestination: PageName | null;
  setPage: (page: PageName) => void;
  navigateTo: (page: PageName, nextParams?: any) => void; // Update signature
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const [page, setPage] = useState<PageName>("home");
  const [params, setParams] = useState<any>(null); // New state to hold IDs/Data

  const backDestination = useMemo(() => {
      const routes: Partial<Record<PageName, PageName>> = {
        "aircraft-details": "aircraft",
        "rental-details": "rental",
        "list-aircraft": "list-category",
        "rent-aircraft": "list-category",
        "list-category": "dashboard",
        "cart": "home",
        "register": "login",
      };
      return routes[page] || null;
    }, [page]);

  const navigateTo = (destination: PageName, nextParams: any = null) => {
      // 🟢 Interceptăm cazul special "contact"
      if (destination === "contact") {
        setPage("home");
        setParams(nextParams);

        setTimeout(() => {
          const contactSection = document.getElementById("contact-section");
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: "smooth", block: "start" });
          } else {
            window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
          }
        }, 100);
        return;
      }

      window.scrollTo(0, 0);
      setParams(nextParams);
      setPage(destination);
    };

  return (
    <NavigationContext.Provider value={{ page, params, setPage, backDestination, navigateTo }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const ctx = useContext(NavigationContext);
  if (!ctx) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return ctx;
};